// @ts-check
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { SelectionActions } from '@/js/enums/externalRepairParts';
import { callRegisterPauseAction, callRegisterResumeAction } from '@/js/services/repairs/ApiRepairs';
import { useRepairPartsStore } from '@/js/stores/repairParts';
import { logError } from '@/js/utils/log';
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { useRepairParts } from './useRepairParts';

export function useRepairPartsPauseResume() {
  const { worker } = storeToRefs(useRepairPartsStore());
  const { loadActiveWorkers, loadInactiveWorkers } = useRepairParts();

  /** @type {import('vue').Ref<boolean>} */
  const isSelectWorkersPopupOpened = ref(false);

  /**
   * Workers data that are passed to the Select workers popup.
   * @type {import('vue').Ref<import('@erpagroapp/types').Trabajador[]>}
   */
  const selectWorkersData = ref([]);

  /**
   * A list of workers selected in the 'Select the part workers' popup.
   * @type {import('vue').Ref<import('@erpagroapp/types').Trabajador[]>}
   */
  const selectedWorkers = ref([]);

  /**
   * State of the last action the user have done in a part.
   * @type {import('vue').Ref<number | null>}
   */
  const selectedAction = ref(null);

  /** @type {import('vue').Ref<boolean>} */
  const isSelectWorkPositionPopupOpened = ref(false);

  /**
   * Stores the selected work position for the 'Select the work position' popup.
   * @type {import('vue').Ref<import('@erpagroapp/types').PuestoTrabajo | null>}
   */
  const selectedWorkPosition = ref(null);

  /**
   * @param {number} workPartId - The ID of the work part.
   * @param {number} activeWorkersNum - The number of active workers in the part.
   * @returns {Promise<PartialDeep<import('@erpagroapp/types').DatosParteReparacion> | null>}
   * The repair part data, or `null` if the pause action still needs to be handled.
   */
  async function handlePauseActionNextStep(workPartId, activeWorkersNum) {
    if (!worker.value) {
      return null;
    }

    if (selectedAction.value !== SelectionActions.PAUSE) {
      selectedAction.value = SelectionActions.PAUSE;
      selectedWorkers.value = [];
      selectedWorkPosition.value = null;

      return handlePauseActionNextStep(workPartId, activeWorkersNum);
    }

    if (activeWorkersNum > 1 && selectedWorkers.value.length === 0) {
      selectWorkersData.value = await loadActiveWorkers(workPartId);
      isSelectWorkersPopupOpened.value = true;
      return null;
    }

    if (!selectedWorkPosition.value) {
      if (selectedWorkers.value.length > 0) {
        isSelectWorkersPopupOpened.value = false;
      }

      isSelectWorkPositionPopupOpened.value = true;
      return null;
    }

    if (selectedWorkers.value.length === 0) {
      // There is 1 expected worker
      selectedWorkers.value = [worker.value];
    }

    return {
      ParteProduccion: {
        Id: workPartId,
        ParteProduccionReal: {
          Trabajadores: selectedWorkers.value.map((item) => ({
            Trabajador: {
              Id: item.Id,
            },
            PuestoTrabajo: {
              Id: selectedWorkPosition.value?.Id ?? 0,
            },
          })),
        },
      },
    };
  }

  /**
   * Registers a pause action for the selected part and workers.
   * @param {Record<string, any>} repairPartData - The repair part data.
   * @param {object} options - The options.
   * @param {string} [options.preloaderMsg] - The preloader message.
   * @param {string} [options.successMsg] - The success message.
   * @param {string} [options.errorMsg] - The error message.
   * @returns {Promise<boolean>} `true` if the operation was successful, `false` otherwise.
   */
  async function registerPauseAction(repairPartData, { preloaderMsg, successMsg, errorMsg }) {
    HiDialog.Preloader(true, preloaderMsg);

    let successful = false;
    try {
      const { data } = await callRegisterPauseAction(repairPartData);

      if (data.Valor === true) {
        HiNotification.Toast(successMsg);
        successful = true;
      } else {
        HiNotification.Toast(data.Mensaje || errorMsg);
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast(errorMsg);
    } finally {
      HiDialog.Preloader(false);

      isSelectWorkPositionPopupOpened.value = false;
      selectedWorkers.value = [];
      selectedWorkPosition.value = null;
    }

    return successful;
  }

  /**
   * Registers route resume status change.
   * @param {Record<string, any>} repairPartData - The repair part data.
   * @param {object} options - The options.
   * @param {string} [options.preloaderMsg] - The preloader message.
   * @param {string} [options.successMsg] - The success message.
   * @param {string} [options.errorMsg] - The error message.
   * @returns {Promise<boolean>} `true` if the operation was successful, `false` otherwise.
   */
  async function registerResumeAction(repairPartData, { preloaderMsg, successMsg, errorMsg }) {
    HiDialog.Preloader(true, preloaderMsg);

    let successful = false;
    try {
      const { data } = await callRegisterResumeAction(repairPartData);

      if (data.Valor === true) {
        HiNotification.Toast(successMsg);
        successful = true;
      } else {
        HiNotification.Toast(data.Mensaje || errorMsg);
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast(errorMsg);
    } finally {
      HiDialog.Preloader(false);

      isSelectWorkersPopupOpened.value = false;
      isSelectWorkPositionPopupOpened.value = false;
      selectedWorkers.value = [];
    }

    return successful;
  }

  /**
   * @param {number} workPartId - The ID of the work part.
   * @param {number} activeWorkersNum - The number of active workers in the part.
   * @param {number} expectedWorkersNum - The number of expected workers in the part.
   * @param {boolean} askWorkPosition - Whether to ask for the work position.
   * @returns {Promise<PartialDeep<import('@erpagroapp/types').DatosParteReparacion> | null>}
   * The repair part data, or `null` if the pause action still needs to be handled.
   */
  async function handleResumeActionNextStep(workPartId, activeWorkersNum, expectedWorkersNum, askWorkPosition) {
    if (selectedAction.value !== SelectionActions.RESUME) {
      selectedAction.value = SelectionActions.RESUME;
      selectedWorkers.value = [];

      return handleResumeActionNextStep(workPartId, activeWorkersNum, expectedWorkersNum, askWorkPosition);
    }

    const inactiveWorkersNum = expectedWorkersNum - activeWorkersNum;
    if (inactiveWorkersNum > 1 && selectedWorkers.value.length === 0) {
      selectWorkersData.value = await loadInactiveWorkers(workPartId);
      isSelectWorkersPopupOpened.value = true;
      return null;
    }

    if (askWorkPosition && !selectedWorkPosition.value) {
      if (selectedWorkers.value.length > 0) {
        isSelectWorkersPopupOpened.value = false;
      }
      isSelectWorkPositionPopupOpened.value = true;
      return null;
    }

    if (selectedWorkers.value.length === 0 && worker.value) {
      selectedWorkers.value = [worker.value];
    }

    return {
      ParteProduccion: {
        Id: workPartId,
        ParteProduccionReal: {
          Trabajadores: selectedWorkers.value.map((item) => ({
            Trabajador: {
              Id: item.Id,
            },
            PuestoTrabajo: (!askWorkPosition || !selectedWorkPosition.value) ? null : {
              Id: selectedWorkPosition.value.Id,
            },
          })),
        },
      },
    };
  }

  return {
    isSelectWorkersPopupOpened,
    selectWorkersData,
    selectedWorkers,
    selectedAction,
    isSelectWorkPositionPopupOpened,
    selectedWorkPosition,
    registerPauseAction,
    handlePauseActionNextStep,
    registerResumeAction,
    handleResumeActionNextStep,
  };
}
